import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import ShareIcon from "@mui/icons-material/Share";
import { colors } from "../../../utils";
import { API_BASE_PATH, API_PATH, ASSETS } from "../../../utils/config";
import { NOTIF_CENTER_ENABLED } from "../../../constants/featureFlags";
import HomeNavEntry from "./HomeNavEntry";
import CreateWheelNavEntry from "./CreateWheelNavEntry";
import { useBreakpoint } from "../../../utils/breakpoints";
import CaptureNavEntry from "./CaptureNavEntry";
import ChatNavEntry from "./ChatNavEntry";
import ExploreNavEntry from "./ExploreNavEntry";
import { DISCOVER_PATH } from "../../DiscoverEntry/discoverEntryTarget";
import ProfileMenu from "../../ProfileMenu";
import SearchBar from "../../SearchBar";
import WaitlistFormModal from "../../WaitlistModal";
import {
  Beta,
  FlexContainer,
  HEADER_ICON_SIZE,
  Icon,
  IconWrapper,
  LOGO_WIDTH_PX,
  LOGO_HEIGHT_PX,
  LogoContainer,
  LogoImg,
  MainContainer,
  NavRegion,
  SecondaryActions,
} from "./styles";
import { HeaderFoldRows } from "./HeaderOverflowMenu";
import { useDispatch, useSelector } from "react-redux";
import { getLoggedInUser, isUserLoggedIn } from "../../../utils/helpers";
import { getOwnProfileIfNeededAction } from "../../../redux/actions/user";
import useAccountAvatarWheelSegments from "../../../hooks/useAccountAvatarWheelSegments";
import NotificationsNoneIcon from "@mui/icons-material/Notifications";
import NotificationDropdown from "../../Notifications/NotificationDropDown";
import NotificationCenter from "../../Notifications/NotificationCenter";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { toast } from "react-toastify";
import { onMessageListener } from "../../../utils/firebase";
import {
  setNewNotificationTrue,
  addNotification,
  clearNewNotification,
  setNotifications,
} from "../../../redux/reducers/notifications";
import { getNotifications } from "../../../services/api";
import { Button } from "@mui/material";

const GalleryHeader = React.forwardRef(
  (
    {
      isJoyrideOpen,
      wheelUpdatedDataSorted,
      isModalOpen,
      // Opt-in "Create Wheel" CTA (Gordon 2026-08-18, option B). Default OFF so
      // the Gallery header is byte-identical to today; Discover (ExploreV2)
      // passes it true to keep the CTA its old bespoke header carried, without
      // forking the shared header.
      showCreateWheel = false,
      // bug #947: gallery/profile id for the "Share Gallery" header button.
      // Only the Gallery page call site (src/pages/Gallery/index.jsx) can
      // resolve one, so every other GalleryHeader mount leaves this
      // undefined and the button stays hidden (see the render below).
      profileId,
      // T22.232 (UX-32): the phone View Wheel page mounts this SAME header
      // (via MobileView/SearchHeader) inside its own <header> banner, so it
      // must not render a second, nested <header> there (`landmark={false}`),
      // and it fills that page's taller 10svh row (`fillHeight`) so the
      // wheel page's slide/loader geometry is unchanged.
      landmark = true,
      fillHeight = false,
    },
    ref
  ) => {
    const [openWaitlistModal, setOpenWaitlistModal] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const profileMenuRef = React.useRef();

    const nav = useNavigate();

    const newNotification = useSelector(
      (state) => state.notifications?.newNotification
    );

    const dispatch = useDispatch();

    const isLoggedIn = isUserLoggedIn();

    // T22.232 (UX-32, audit-10150254): ONE phone header everywhere. Below the
    // phone cut Create Wheel always renders, so /settings, the View Wheel
    // page (MobileView/SearchHeader) and every other mount match Discover
    // instead of dropping it and sliding the bell/⋮ right (156->220 measured
    // on main.405cea26 @390). Above the cut it stays opt-in (showCreateWheel).
    const { isPhone } = useBreakpoint();
    const renderCreateWheel = showCreateWheel || isPhone;

    // Discover and other bare GalleryHeader mounts never passed wheel segments,
    // so ProfileMenu stayed on the generic wheel frame until Gallery loaded
    // primaryWheel into redux. Callers that supply their own prop (Gallery page,
    // MessagesLayout) keep that override; everyone else gets the shared hook.
    const hasExternalWheelSegments = wheelUpdatedDataSorted !== undefined;
    const internalAccountAvatarWheelSegments = useAccountAvatarWheelSegments({
      enabled: !hasExternalWheelSegments,
    });
    const resolvedWheelUpdatedDataSorted = hasExternalWheelSegments
      ? wheelUpdatedDataSorted
      : internalAccountAvatarWheelSegments;

    // Login only seeds `{ is2faEnabled }` into redux; the full profile (incl.
    // profile_image for the menu portrait) arrives via getUserProfileById.
    // routes.js dispatches that globally, but a cold Discover landing after
    // cache clear can paint the header before that fetch completes — ensure the
    // shared header always requests the profile when a session is present.
    useEffect(() => {
      if (!isLoggedIn) return;
      const userId = getLoggedInUser()?.id;
      if (userId) {
        // fix-fe-5107-own-profile-duplicate-fetch-regressed: this "ensure it's
        // requested" fallback is passive, not a guaranteed refetch — skip if
        // routes.js's app-shell effect (or any other mount site) already got
        // it (see getOwnProfileIfNeededAction, redux/actions/user.js).
        dispatch(getOwnProfileIfNeededAction(userId));
      }
    }, [dispatch, isLoggedIn]);

    React.useImperativeHandle(ref, () => ({
      openProfileMenu: () => {
        if (profileMenuRef.current) {
          profileMenuRef.current.toggleDropdown();
        }
      },
      closeProfileMenu: () => {
        if (profileMenuRef.current) {
          profileMenuRef.current.closeDropdown();
        }
      },
    }));

    // bug #947: moved out of WheelGallery/Profile/index.jsx (was the
    // Profile card's "Share Gallery" icon button) into the header, next to
    // Invite. Same native-share-with-clipboard-fallback behavior; only the
    // share title lost the profile username (not available in this
    // component) in favor of a fixed "iYuga Gallery" string.
    const handleShareGallery = async (event) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (!profileId) return;
      // stgfix-1008-B62-texted-copied-invite-and-gallery-share-links-previ:
      // the bare SPA route (`${window.location.origin}/gallery/${profileId}`)
      // is served by S3 + CloudFront with the SAME static index.html for
      // every path -- no Django view ever runs there, so a texted/copied
      // link always unfurled the generic site-wide card, never the owner's
      // name/picture. Copy/share the backend-rendered OG card URL instead
      // (same pattern as wheel share_url / collaboration join_url): a
      // crawler gets real per-owner tags; a human is JS-redirected to the
      // real Gallery page.
      // review-1008-Frontend-6687: API_BASE_PATH ends in "/" and
      // GALLERY_SHARE starts with "/", so a raw join copied
      // "https://app-stg.iyuga.net//api/v2/..." (double slash, only working
      // because nginx happens to merge slashes). Trim before joining.
      const galleryUrl = `${String(API_BASE_PATH || "").replace(/\/+$/, "")}${API_PATH.GALLERY_SHARE(profileId)}`;
      try {
        if (typeof navigator.share === "function") {
          await navigator.share({ title: "iYuga Gallery", url: galleryUrl });
          return;
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        // Fall through to clipboard when the native share sheet isn't available.
      }
      try {
        await navigator.clipboard.writeText(galleryUrl);
        toast.success("Gallery link copied!", {
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (error) {
        console.error("Failed to share gallery:", error);
        toast.error("Failed to copy URL");
      }
    };

    // A5 (a11y): the header logo doubles as a persistent nav control. It must
    // be reachable and operable by keyboard + announced to screen readers, not
    // a bare click-only <div><img/></div>. One handler shared by pointer and
    // keyboard so the two paths can never diverge.
    //
    // QA-149 (Kelly, 2026-08-19): logo click now goes to Discover, not the
    // gallery. Previously the logo was the ONLY way "home" so it pointed at
    // the gallery; since Gordon's 2026-08-18 HomeNavEntry gave the header its
    // own dedicated Home icon (see the icon row below), the logo is free to
    // carry the more typical "brand mark -> discovery feed" behaviour every
    // neighbouring product uses. setIsUserProfile(true) is dropped on this
    // path — that flag means "viewing your OWN gallery", which is no longer
    // what a logo click lands on.
    const handleLogoActivate = () => {
      if (isLoggedIn) {
        // Reset Discover to the unfiltered feed — URL params plus in-page search
        // / Sort & Filter state (ExploreV2 listens for resetDiscover).
        nav({ pathname: DISCOVER_PATH, search: "" }, { state: { resetDiscover: true } });
      } else {
        nav("/login"); // Navigate to login if not logged in
      }
    };

    const handleNotificationsClick = () => {
      setShowDropdown(!showDropdown);
      if (newNotification) {
        dispatch(clearNewNotification());
      }
    };

    const fetchNotifications = async (page = 1) => {
      try {
        const response = await getNotifications(page);
        const newNotifications = response.data.results;
        dispatch(setNotifications(newNotifications));
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        // setLoading(false);
      }
    };

    useEffect(() => {
      // When the flag-gated NotificationCenter is ON it owns the fetch (react-query),
      // so skip this legacy redux poll to avoid the EW-1142 duplicate round-trip.
      // When OFF this remains the only fetch — byte-identical to today.
      //
      // nem2-sweep-notifications: this had no `isLoggedIn` guard (unlike the
      // profile fetch above), so a logged-out visitor on any GalleryHeader-
      // mounting page fired `GET /api/v1/notification/?page=1` anyway — a
      // request an anonymous session can never be authorized for, producing
      // a guaranteed 401. Anonymous users have nothing to fetch here.
      if (!NOTIF_CENTER_ENABLED && isLoggedIn) {
        fetchNotifications(1);
      }
    }, [isLoggedIn]);

    // realtime-notif-offline:R7.3 — "Notification polling fallback." A user
    // whose browser push is denied/unsupported (§0.3: web has zero
    // navigator.onLine hits, and — separately, this cell — zero
    // notification-polling anywhere) previously had NO way to learn about a
    // new notification except opening `/notifications` or reloading; the bell
    // badge simply never updated while the tab sat open. Gated the SAME way
    // this component's own initial fetch already is (only when it — not the
    // flag-gated NotificationCenter — owns fetching, avoiding the EW-1142
    // duplicate-round-trip this file already guards against), and further
    // gated on push genuinely being unavailable so a user WITH working push
    // never pays for a redundant poll. Pauses while the tab is hidden
    // (visibility-aware, matching the DM `chatSocket.js` fallback-poll
    // precedent already established in this codebase for exactly this class
    // of "socket/push unavailable -> fall back to REST" gap).
    //
    // 🟩 BE LOAD FLAG (qa-validator critic finding, 2026-08-06 — needs
    // Usman/Amir's ack before this PR merges, not just FE review): a fresh
    // browser tab's `Notification.permission` reads `"default"` (neither
    // granted nor denied) until the user has actually answered the OS
    // prompt — which per `iyuga-login-automation-hang` and the push-init
    // flow's own fragility (N2.1 etc.) plausibly describes a LARGE fraction
    // of real sessions, not an edge case. That means this poll likely runs
    // for MOST logged-in users, all adding a recurring `GET
    // /api/v1/notification/?page=1` load against a BE already flagged
    // EC2-OOM-sensitive on merges (`be-sandbox-merge-heavy-deploy-oom`).
    // Interval set conservatively at 60s (not the initial 30s) pending that
    // review; 🟩 Usman/Amir should confirm this additive load is acceptable
    // — or that a cheaper aggregate-unread endpoint should gate it instead —
    // before merge.
    // fix-fe-5118-mount-only-guard: this effect kept its pre-existing `[]`
    // (mount-only) dependency array when the `isLoggedIn` check above was
    // added, so `isLoggedIn` was read from a closure captured once at mount
    // and never re-evaluated. Two ways that goes wrong on this SPA, which
    // never remounts GalleryHeader across a login/logout: (1) a session that
    // logs IN after the header already mounted anonymously never starts the
    // fallback poll at all; (2) a session that logs OUT while the poll is
    // already running keeps firing `fetchNotifications` — i.e. exactly the
    // anon-401 poll this PR's other effect was fixed to stop. `isLoggedIn`
    // now belongs in the dependency array so a login/logout tears down and
    // restarts (or stops) the interval like the mount effect above already
    // does.
    useEffect(() => {
      if (NOTIF_CENTER_ENABLED || !isLoggedIn) return undefined;
      const pushUnavailable =
        typeof window === "undefined" ||
        !("Notification" in window) ||
        window.Notification.permission !== "granted";
      if (!pushUnavailable) return undefined;

      const POLL_MS = 60000;
      const intervalId = setInterval(() => {
        if (document.visibilityState === "visible") {
          fetchNotifications(1);
        }
      }, POLL_MS);

      return () => clearInterval(intervalId);
    }, [isLoggedIn]);

    return (
      // G3/G4 (coverage doc, LIVE-measured nav=0 / banner=0 on /settings,
      // /analytics, /referrals and every other authenticated surface): this
      // IS the actual persistent header (Wrapper renders GalleryHeader, not
      // Navbar). `as="header"` at document top-level carries the implicit
      // `role=banner` landmark; NavRegion (`display:contents`, see styles.js)
      // wraps the real controls in a `<nav>` landmark without adding a 3rd
      // flex item to MainContainer's 2-item flex layout.
      <MainContainer
        as={landmark ? "header" : "div"}
        style={fillHeight ? { height: "100%" } : undefined}
      >
      <NavRegion aria-label="Primary">
        <FlexContainer>
          <LogoContainer
            role="button"
            tabIndex={0}
            aria-label={isLoggedIn ? "iYuga home — go to Discover" : "iYuga — go to login"}
            onClick={handleLogoActivate}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleLogoActivate();
              }
            }}
          >
            <LogoImg
              src={ASSETS.iyugaBetaLogo}
              height={LOGO_HEIGHT_PX}
              width={LOGO_WIDTH_PX}
              alt="iYuga"
            />
          </LogoContainer>
          {isLoggedIn && <SearchBar />}
        </FlexContainer>
        {/* <CenterContainer>
        <AddWheelButton>
          <a href="/builder">
            <img
              src={ASSETS.segmentEditor.AddWheel}
              height={60}
              width={60}
              alt="Add Wheel"
            />
          </a>
        </AddWheelButton>
      </CenterContainer> */}
        {/* header-390-overflow-reopened: `noShrink` keeps this right-hand
            cluster (bell / create-wheel / ⋮ trigger / avatar) pinned at its
            natural content width so MainContainer can never squeeze it
            narrower than that — see the note on FlexContainer in styles.js. */}
        <FlexContainer noShrink>
          {isLoggedIn ? (
            <>
              {/* <IconWrapper>
          <Icon src={ASSETS.segmentEditor.NotificationIcon} />
        </IconWrapper> */}
              {/* ISP-4018 phone header fold. Above the phone cut this wrapper
                  is `display: contents` — it generates NO box, so these icons
                  stay direct flex children of FlexContainer and tablet/desktop
                  geometry is unchanged. Below the cut it is `display: none` and
                  the same actions render as labeled rows in HeaderOverflowMenu.
                  That is what removes the measured 226px horizontal overflow at
                  390px (this cluster alone needed 444px of a 376px client
                  width) — see GalleryHeader.phoneFold.test.jsx. */}
              <SecondaryActions>
                {/* The Feedback entry was REMOVED from the header (Gordon,
                    2026-08-01, asked repeatedly): the header is prime real
                    estate and the same form is already reachable from
                    Settings › FAQ › "Feedback form", which opens this exact
                    SocialFoxFeedbackForm. It was removed from BOTH surfaces —
                    this icon cluster and the phone HeaderOverflowMenu — because
                    removing only the desktop one would have left it alive below
                    the phone cut. See GalleryHeader.noFeedbackEntry.test.jsx. */}

                {/* Capture / Notepad inbox (WL-021) — first-class, discoverable
                    header entry (fixes ISP-4068 B "no entry point"). Extracted to
                    the self-gating CaptureNavEntry shared component (renders null
                    while CAPTURE_ENABLED is off) so the nav path is independently
                    testable; the rendered DOM is unchanged. */}
                {/* ISP-9999 / QA-149 (Kelly, 2026-08-19): header icon order is
                    now Home -> Discover -> Capture -> Messages -> Notifications
                    (NotificationCenter renders immediately after this cluster,
                    below) -> Share. Previously Home -> Capture -> Messages ->
                    Discover; QA-149 asked for Discover to lead the secondary
                    cluster right after Home. */}
                {/* Home (Gallery) — the user's home base as a persistent
                    top-nav icon (Gordon 2026-08-18). Not flag-gated; leads the
                    nav cluster. Line-art house (ISP-9999, 2026-08-27) per the
                    icon law — matches the mobile app's gallery tab icon. */}
                <HomeNavEntry />
                {/* Discover (DL-098) — the persistent top-nav entry Gordon's
                    Discover spec asks for. Measured 2026-08-01: there was none,
                    so /explore was reachable only by typing the URL. Not
                    flag-gated: /explore is live in every environment. Label
                    renamed Explore -> Discover, Gordon 2026-08-06. */}
                <ExploreNavEntry />
                <CaptureNavEntry />
                {/* GR-2: Facebook-style DM entry point — clicking toggles the
                    bottom-right MiniDmDock (the docked chat box) instead of the
                    full /messages page. Self-gates on CHAT_ENABLED (renders null
                    while OFF), so the header is byte-identical to today until an
                    environment opts in. */}
                <ChatNavEntry />
                {/* Following (Follower Feed) icon — REMOVED from the header
                    (Gordon, 2026-08-06). The FollowerFeedNavEntry component,
                    the FOLLOWER_FEED_ENABLED flag and the /following route are
                    untouched (see GalleryHeader.noFollowingEntry.test.jsx) —
                    only the header's clickable entry point is gone, on both the
                    desktop cluster (here) and the phone overflow menu
                    (HeaderOverflowMenu.jsx). */}
              </SecondaryActions>
              {/* {newNotification ? (
            <FiberManualRecordIcon
              style={{
                color: "orange",
                fontSize: "14px",
                marginRight: "-27px",
                marginTop: "-15px",
                zIndex: 1000,
              }}
            />
          ) : null}
          <NotificationsNoneIcon
            onClick={handleNotificationsClick}
            style={{
              cursor: "pointer",
              color: colors.PrimaryPurple,
              marginRight: "10px",
              fontSize: "30px",
            }}
          /> */}
              {NOTIF_CENTER_ENABLED && <NotificationCenter />}
              {showDropdown && (
                <NotificationDropdown onClose={() => setShowDropdown(false)} />
              )}
              <div style={{ display: "flex", alignItems: "center" }}>
                {/* bug #947: "Share Gallery" moved here from the Profile
                    card's action row — visible to owner AND visitor alike
                    (unlike Invite, right below, which is owner-only),
                    matching the Profile card's old, unconditional rendering
                    of this button. Second-to-last header button, right
                    before Invite. */}
                {profileId && (
                  <SecondaryActions>
                    <Tooltip title="Share Gallery">
                      <IconWrapper
                        className="share-gallery-button"
                        role="button"
                        tabIndex={0}
                        aria-label="Share Gallery"
                        onClick={handleShareGallery}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleShareGallery(e);
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <ShareIcon
                          aria-hidden="true"
                          style={{
                            color: colors.PrimaryPurple,
                            // header-tighter-messenger-parity-0916: was a lone 22px.
                            fontSize: HEADER_ICON_SIZE,
                          }}
                        />
                      </IconWrapper>
                    </Tooltip>
                  </SecondaryActions>
                )}
                {/* bug #944: the standalone Themes/palette header button was
                    removed — Themes is reachable ONLY from the avatar/
                    profile dropdown's existing "Themes" row (ProfileMenu ->
                    ThemesPopup), so there is a single entry point instead of
                    two. */}
                {/* ISP-9999 / gordon-0831-remove-header-invite-button
                    (Gordon, 2026-08-31, verbatim: "Get rid of that Invite
                    button from the header entirely"): the envelope Invite
                    button (desktop bar, above) and its HeaderOverflowMenu
                    "Invite Friends" fold-row (phone/tablet) are both gone.
                    The ShareInviteModal flow itself is untouched and still
                    reachable from WheelHeader on the View Wheel page (see
                    src/pages/ViewWheel/index.jsx and
                    src/pages/ViewWheel/MobileView/index.jsx) — only this
                    header's entry point was removed, not the feature. */}
                {/* T22.615 (Gordon, 2026-10-21): the separate ⋮ "More actions"
                    menu is gone — below the tabletP cut its rows (Discover,
                    Capture, Messages, Share Gallery) render INSIDE the avatar
                    dropdown via ProfileMenu's foldedNav, so the phone header
                    has ONE menu and Gallery is listed once (the avatar menu's
                    own Gallery row). See GalleryHeader.oneMenu.t22615.test.jsx. */}
                {/* Opt-in Create-Wheel CTA — renders only on surfaces that pass
                    showCreateWheel. See CreateWheelNavEntry.
                    T22.265 (Gordon, 2026-10-14): Create Wheel is ALWAYS the
                    last item before the avatar; Share Gallery sits to its
                    LEFT. It used to render before this cluster, so Share
                    landed between Create Wheel and the avatar. See
                    GalleryHeader.createWheelLastBeforeAvatar.t22265.test.jsx. */}
                {renderCreateWheel && <CreateWheelNavEntry />}
                <ProfileMenu
                  ref={profileMenuRef}
                  className="profile-menu"
                  isJoyrideOpen={isJoyrideOpen}
                  wheelUpdatedDataSorted={resolvedWheelUpdatedDataSorted}
                  isModalOpen={isModalOpen}
                  foldedNav={(closeMenu) => (
                    <HeaderFoldRows
                      includeHome={false}
                      closeMenu={closeMenu}
                      onShareGallery={handleShareGallery}
                      showShareGallery={!!profileId}
                    />
                  )}
                />
              </div>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => nav("/login")}
              sx={{
                height: 32,
                borderRadius: "8px",
                opacity: 1,
                gap: "8px",
                padding: "6px 10px",
                background: "#8F38EF",
                boxShadow:
                  "0px 2px 5px 0px #B6CD261A, 0px 0px 0px 1px #7A18E6, 0px 1px 1px 0px #00000024, 0px 1px 0px 0px #C590FF inset",
                "&:hover": {
                  background: "#8F38EF", // Keep the same background on hover
                },
              }}
            >
              Login Or Signup
            </Button>
          )}
        </FlexContainer>
      </NavRegion>
        <WaitlistFormModal
          open={openWaitlistModal}
          onClose={() => setOpenWaitlistModal(false)}
        />
      </MainContainer>
    );
  }
);

export default GalleryHeader;
